import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Grid,
} from '@mui/material';
import {
  History as HistoryIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  CreditCard as CreditCardIcon,
  Star as StarIcon,
  ShoppingCart as ShoppingCartIcon,
  AdminPanelSettings as AdminIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { CreditTransaction } from '../types/credits';
import { getCreditTransactions } from '../lib/credits';
import { useCredits } from '../contexts/CreditContext';
import { supabase } from '../lib/supabaseClient';

const CreditHistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { credits } = useCredits();
  
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const data = await getCreditTransactions(user.id);
      setTransactions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'PURCHASE':
        return <ShoppingCartIcon sx={{ color: '#10b981' }} />;
      case 'BONUS':
        return <StarIcon sx={{ color: '#fbbf24' }} />;
      case 'USAGE':
        return <TrendingDownIcon sx={{ color: '#ef4444' }} />;
      case 'REFUND':
        return <RefreshIcon sx={{ color: '#3b82f6' }} />;
      case 'ADMIN_ADJUSTMENT':
        return <AdminIcon sx={{ color: '#8b5cf6' }} />;
      default:
        return <CreditCardIcon sx={{ color: '#6b7280' }} />;
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'PURCHASE':
        return '#10b981';
      case 'BONUS':
        return '#fbbf24';
      case 'USAGE':
        return '#ef4444';
      case 'REFUND':
        return '#3b82f6';
      case 'ADMIN_ADJUSTMENT':
        return '#8b5cf6';
      default:
        return '#6b7280';
    }
  };

  const getTransactionLabel = (type: string) => {
    switch (type) {
      case 'PURCHASE':
        return t('credits.history.purchase');
      case 'BONUS':
        return t('credits.history.bonus');
      case 'USAGE':
        return t('credits.history.usage');
      case 'REFUND':
        return t('credits.history.refund');
      case 'ADMIN_ADJUSTMENT':
        return t('credits.history.adminAdjustment');
      default:
        return type;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('bg-BG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount: number) => {
    const sign = amount > 0 ? '+' : '';
    return `${sign}${amount}`;
  };



  if (loading) {
    return (
      <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress sx={{ color: '#28A0F0' }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <Header />
      
      
      <Box sx={{ py: 4 }}>
        <Container maxWidth="lg" sx={{ width: '80%' }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ 
                color: '#a0a0a0', 
                mb: 2,
                '&:hover': { color: '#ffffff' }
              }}
            >
              {t('common.back')}
            </Button>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <HistoryIcon sx={{ color: '#28A0F0', fontSize: 32 }} />
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  color: '#ffffff',
                  fontWeight: 700,
                }}
              >
                {t('credits.history.title')}
              </Typography>
            </Box>
            
            <Typography variant="h6" sx={{ color: '#a0a0a0' }}>
              {t('credits.history.subtitle')}
            </Typography>
          </Box>

          {/* Current Balance Card */}
          {credits && (
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
                border: '1px solid rgba(40, 160, 240, 0.3)',
                borderRadius: 3,
                mb: 4,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CreditCardIcon sx={{ color: '#28A0F0', fontSize: 32 }} />
                      <Box>
                        <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700 }}>
                          {credits.credits_balance}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          {t('credits.currentBalance')}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', gap: 3, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                      <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                        <Typography variant="h6" sx={{ color: '#10b981', fontWeight: 600 }}>
                          {transactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          {t('credits.history.totalEarned')}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                        <Typography variant="h6" sx={{ color: '#ef4444', fontWeight: 600 }}>
                          {Math.abs(transactions.filter(t => t.amount < 0).reduce((sum, t) => sum + t.amount, 0))}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          {t('credits.history.totalSpent')}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 4, backgroundColor: 'rgba(244, 67, 54, 0.1)', color: '#ffffff' }}>
              {error}
            </Alert>
          )}

          {/* Transactions */}
          <Card
            sx={{
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    {t('credits.history.transactions')}
                  </Typography>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={loadTransactions}
                    sx={{ color: '#28A0F0' }}
                  >
                    {t('common.refresh')}
                  </Button>
                </Box>
              </Box>

              {transactions.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <HistoryIcon sx={{ color: '#6b7280', fontSize: 64, mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#a0a0a0', mb: 1 }}>
                    {t('credits.history.noTransactions')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    {t('credits.history.noTransactionsDesc')}
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#a0a0a0', fontWeight: 600, borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                          {t('credits.history.type')}
                        </TableCell>
                        <TableCell sx={{ color: '#a0a0a0', fontWeight: 600, borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                          {t('credits.history.description')}
                        </TableCell>
                        <TableCell sx={{ color: '#a0a0a0', fontWeight: 600, borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                          {t('credits.history.amount')}
                        </TableCell>
                        <TableCell sx={{ color: '#a0a0a0', fontWeight: 600, borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                          {t('credits.history.balance')}
                        </TableCell>
                        <TableCell sx={{ color: '#a0a0a0', fontWeight: 600, borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                          {t('credits.history.date')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((transaction) => (
                        <TableRow key={transaction.id} hover>
                          <TableCell sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getTransactionIcon(transaction.type)}
                              <Chip
                                label={getTransactionLabel(transaction.type)}
                                size="small"
                                sx={{
                                  backgroundColor: `${getTransactionColor(transaction.type)}20`,
                                  color: getTransactionColor(transaction.type),
                                  border: `1px solid ${getTransactionColor(transaction.type)}40`,
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: '#ffffff', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                            {transaction.description}
                          </TableCell>
                          <TableCell sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                            <Typography
                              variant="body1"
                              sx={{
                                color: transaction.amount > 0 ? '#10b981' : '#ef4444',
                                fontWeight: 600,
                              }}
                            >
                              {formatAmount(transaction.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: '#a0a0a0', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                            {/* Calculate running balance from current balance backwards */}
                            {credits?.credits_balance ? 
                              credits.credits_balance - transactions
                                .slice(transactions.indexOf(transaction) + 1)
                                .reduce((sum, t) => sum + t.amount, 0)
                              : 0
                            }
                          </TableCell>
                          <TableCell sx={{ color: '#a0a0a0', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                            {formatDate(transaction.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>

    </Box>
  );
};

export default CreditHistoryPage;
